import type { NormPoint } from './types';
import { INTERP_MAX_STEP } from './config';

/**
 * Stroke interpolation. Pointer events during fast finger movement arrive
 * sparsely; without interpolation a stroke renders as disconnected dots.
 * `interpolatePoints` inserts intermediate points so no gap between consecutive
 * samples exceeds `maxStep` (normalized distance).
 */
export function interpolatePoints(
  points: readonly NormPoint[],
  maxStep: number = INTERP_MAX_STEP,
): NormPoint[] {
  if (points.length === 0) return [];
  if (points.length === 1) return [{ ...points[0] }];

  const out: NormPoint[] = [{ ...points[0] }];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dist = Math.hypot(dx, dy);
    if (dist > maxStep) {
      const steps = Math.ceil(dist / maxStep);
      for (let s = 1; s < steps; s++) {
        const t = s / steps;
        out.push({ x: a.x + dx * t, y: a.y + dy * t });
      }
    }
    out.push({ x: b.x, y: b.y });
  }
  return out;
}

/**
 * Accumulates raw pointer samples for a single stroke that is LOCKED to one
 * region. The builder never changes region once created. It de-duplicates
 * identical consecutive samples and can produce the final interpolated path.
 */
export class StrokeBuilder {
  private readonly raw: NormPoint[] = [];

  constructor(private readonly maxStep: number = INTERP_MAX_STEP) {}

  /** Add a raw sample. Ignores exact duplicates of the previous sample. */
  addPoint(p: NormPoint): void {
    const last = this.raw[this.raw.length - 1];
    if (last && last.x === p.x && last.y === p.y) return;
    this.raw.push({ x: p.x, y: p.y });
  }

  get rawCount(): number {
    return this.raw.length;
  }

  isEmpty(): boolean {
    return this.raw.length === 0;
  }

  /** Raw samples so far (defensive copy). */
  rawPoints(): NormPoint[] {
    return this.raw.map((p) => ({ ...p }));
  }

  /** Final interpolated path suitable for storing in a DrawOperation. */
  build(): NormPoint[] {
    return interpolatePoints(this.raw, this.maxStep);
  }
}
